import Netx, {dispatcher} from '../bootstrap';
import View from './netx-view';
import _ from 'underscore';

import type AppView from '@netx/core-app/models/appView';
import type Collection from '@netx/core/lib/netx-collection';
import type Model from '@netx/core/lib/netx-model';

import {safeReplaceInArray} from '../utils/safeReplaceInArray';
export {safeReplaceInArray} from '../utils/safeReplaceInArray';

import {simulateClickBody} from '../utils/simulateClickBody';
export {simulateClickBody} from '../utils/simulateClickBody';

/**
 * Debounce for promises
 * @param fn The async function to debounce
 * @param ms Time in milliseconds
 */
export const debouncePromise = <
	T extends (...args: any[]) => Promise<any> | void
>(
	fn: T,
	ms = 0,
) => {
	let timeoutId: NodeJS.Timeout;
	const pending: {
		resolve(value?: any): void;
		reject(reason?: any): void;
	}[] = [];
	return (...args: Parameters<T>): ReturnType<T> =>
		new Promise((resolve, reject) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				const currentPending = [...pending];
				pending.length = 0;
				Promise.resolve(fn.apply(this, args)).then(
					(data) => currentPending.forEach(({resolve}) => resolve(data)),
					(error) => currentPending.forEach(({reject}) => reject(error)),
				);
			}, ms);
			pending.push({resolve, reject});
		}) as ReturnType<T>;
};

export const deepObjectExtend = (target: Object, source: Object) => {
	for (const prop in source) {
		if (source.hasOwnProperty(prop)) {
			if (target[prop] && typeof source[prop] === 'object') {
				deepObjectExtend(target[prop], source[prop]);
			} else {
				target[prop] = source[prop];
			}
		}
	}
	return target;
};

export const getViewModel = <TModel extends Model = Model>(
	view: View<TModel | AppView<TModel>>,
): TModel | undefined => {
	const viewData = view.data || {};
	let model = viewData.model || view.model;
	if (model && model.isAppView) {
		model = model.viewModel as TModel;
	}
	return !model ? undefined : (model as TModel);
};

export const getViewCollection = <TCollection extends Collection = Collection>(
	view: View<any, TCollection>,
): TCollection | undefined => {
	const viewData = view.data || {};
	const collection = viewData.collection || view.collection;
	return !collection ? undefined : (collection as TCollection);
};

/**
 * Check if the app is at a portal endpoint
 */
export const isPortal = () => {
	// todo: yarn watch:portal does not have /portals
	return window.location.pathname.match(/^\/portals\//) != null;
};

/**
 * Given an object it will return a protected version that object
 * @function
 * @param {object} object Object
 * @return {Proxy} Object
 */
export const protectObjectProxy = (object) => {
	return new Proxy(object, {
		// Will return any object/arrays as their own copies
		// elminating the ability to pollute items in the object
		get(obj, prop) {
			const value = obj[prop];
			if (typeof value === 'object') return JSON.parse(JSON.stringify(value));
			return value;
		},
		// No setting
		set(obj, prop, value) {
			return false;
		},
	});
};

export const performAction = (actionStr: string, actionArgs: any[] = []) => {
	console.log('perform action', actionStr);
	const {router, Actions} = Netx;

	const action = Actions.Find(actionStr);
	if (!action) throw new Error(`'${actionStr}' is not a registered action`);

	// Generic handler for all Action clicks:
	if (action.fake) {
		console.warn('The action "' + action.event + '" has not been registered.');
	}
	// or else, if the action itself defined a wrapper, run the arguments through that.
	else if (_.isFunction(action.wrapper)) {
		actionArgs = action.wrapper.apply(this, actionArgs);
		if (!_.isArray(actionArgs)) {
			throw new Error(
				'Action wrapper for ' + action.event + ' did not return an array',
			);
		}
	}

	// close any/all open dropdown menus
	dispatcher.trigger('app:close:menus');

	// HTML-1050: Remember when actions are fired.
	Actions.latest = action;
	Actions.history = Actions.history || [];
	Actions.history.push(action);

	// If this action has a route, route to it
	if (_.isFunction(action.route)) {
		// route can be a function that takes args & returns a string
		router.navigate(action.route.apply(action, actionArgs), true);
	} else if (_.isString(action.route)) {
		// it can also be a plain string.
		// TODO: such actions could be rendered as simple hrefs that can be enabled/disabled, so the browser can do this part ...
		router.navigate(action.route, true);
	}
	// If this action has a link, send the browser to it.
	// (Again, this could have been rendered as a simple href ...)
	else if (_.isString(action.link)) {
		// a link could be of the format "#route", or "/other/site/on/this/host" or even "http://google.com"
		window.location = action.link;
	}
	// If no view & no route, trigger this action's event
	else {
		actionArgs.unshift(action.event);
		dispatcher.trigger.apply(dispatcher, actionArgs);
	}
};

/**
 * Purge elements from selection
 * @function
 * @param {array} els Elements selected
 * @param {array} purgeEls Elements to be purged from
 */
export const purgeEls = (els: Element[], purgeEls: Element[]) => {
	const purgeCount = purgeEls.length;
	if (purgeCount === 0) return els;

	return els.filter((el) => {
		for (let i = 0; i < purgeCount; i++) {
			if (purgeEls[i].contains(el)) return false;
		}
		return true;
	});
};

export const executeOnOrReturn = function <T>(
	value: T,
	executor?: (value: T) => T,
) {
	if (executor) return executor(value);
	return value;
};

export const addToArrayUnique = function <T>(array: T[], toAdd: T) {
	if (array.includes(toAdd)) return array;
	array.push(toAdd);
	return array;
};

export const isTouchEnabled = () =>
	'ontouchstart' in window || navigator?.maxTouchPoints > 0;

export const safeAddToArrayUnique = function <T>(array: T[], toAdd: T) {
	return addToArrayUnique([...array], toAdd);
};

export const safeAddOrReplaceInArray = function <T>(
	array: T[],
	index: number,
	replaceWith: T,
) {
	if (index > -1) return safeReplaceInArray(array, index, replaceWith);
	return [...array].concat([replaceWith]);
};

export const safeToggleArrayValue = function <T>(array: T[], value: T) {
	const index = array.indexOf(value);
	const newArray = [...array];

	index === -1 ? newArray.push(value) : newArray.splice(index, 1);

	return newArray;
};

/**
 * Convert seconds to HH:MM:SS
 * If seconds exceeds 24 hours, hours will be greater than 24 (30:05:10)
 *
 * @param seconds
 * @returns hh:mm:ss
 */
export const secondsToHms = (seconds: number): string => {
	const days = Math.floor(seconds / 86400);
	const remainderSeconds = seconds % 86400;
	const hms = new Date(remainderSeconds * 1000).toISOString().substring(11, 19);
	return hms.replace(/^(\d+)/, (h) =>
		`${Number(h) + days * 24}`.padStart(2, '0'),
	);
};

export default {
	getViewCollection,
	getViewModel,
	performAction,
	protectObjectProxy,
	purgeEls,
	secondsToHms,
	simulateClickBody,
};
